import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useParams } from '@tanstack/react-router';
import {
  BROADCAST_OVERLAY_POLL_INTERVAL_MS,
  DRAW_CULMINATION_CANDIDATE_MAX,
  DrawRevealKind,
  progressiveShortlistPayloadSchema,
  type BroadcastDrawState,
  type DrawRevealStep,
  type ProgressiveShortlistPayload,
} from '@qaytim/shared';

import { strings } from '@/i18n/strings';
import { fetchBroadcastDrawState } from '@/lib/broadcast-api';

const FRAME_HOLD_MS = 800;
const FIXED_TICKET_PREFIX_LENGTH = 'QT-'.length;
const COUNTDOWN_TICK_MS = 1_000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;

interface ProgressiveState {
  payload: ProgressiveShortlistPayload;
  seq: number;
}

interface PlaybackFrame {
  state: BroadcastDrawState;
  /** Only this newly received step may animate character content. */
  animatedStepSeq: number | null;
}

/** Hash accepts both browser (`#token=...`) and TanStack (`token=...`) representations. */
function overlayTokenFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const token = params.get('token')?.trim();
  return token ? token : null;
}

function latestProgressiveState(steps: DrawRevealStep[]): ProgressiveState | null {
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const step = steps[index];
    if (step?.kind !== DrawRevealKind.SHORTLIST) continue;

    const parsed = progressiveShortlistPayloadSchema.safeParse(step.payload);
    if (parsed.success) return { payload: parsed.data, seq: step.seq };
  }
  return null;
}

function hasStep(state: BroadcastDrawState, kind: DrawRevealKind): boolean {
  return state.steps.some((step) => step.kind === kind);
}

function ticketCountFromState(state: BroadcastDrawState): number | null {
  if (!hasStep(state, DrawRevealKind.TICKET_COUNT)) return null;

  for (let index = state.steps.length - 1; index >= 0; index -= 1) {
    const step = state.steps[index];
    if (step?.kind !== DrawRevealKind.TICKET_COUNT) continue;
    const count = step.payload['ticketCount'];
    if (typeof count === 'number' && Number.isInteger(count) && count >= 0) return count;
  }

  return state.draw.ticketCount;
}

function sortedState(state: BroadcastDrawState): BroadcastDrawState {
  return { ...state, steps: [...state.steps].sort((left, right) => left.seq - right.seq) };
}

function frameThroughStep(state: BroadcastDrawState, step: DrawRevealStep): PlaybackFrame {
  return {
    state: {
      ...state,
      steps: state.steps.filter((candidate) => candidate.seq <= step.seq),
      lastSeq: step.seq,
    },
    animatedStepSeq: step.seq,
  };
}

/**
 * Converts coarse polling snapshots into an ordered broadcast timeline.
 *
 * Startup fast-forwards because an OBS source may be refreshed mid-show. After that, every unseen
 * server step becomes a frame and stays on air before the next queued frame may replace it.
 */
function useBroadcastPlayback(
  latestState: BroadcastDrawState | undefined,
  streamKey: string,
): PlaybackFrame | null {
  const [frame, setFrame] = useState<PlaybackFrame | null>(null);
  const [queue, setQueue] = useState<PlaybackFrame[]>([]);
  const [isHolding, setIsHolding] = useState(false);
  const streamKeyRef = useRef(streamKey);
  const highestSeenSeqRef = useRef<number | null>(null);

  useEffect(() => {
    if (streamKeyRef.current !== streamKey) {
      streamKeyRef.current = streamKey;
      highestSeenSeqRef.current = null;
      setFrame(null);
      setQueue([]);
      setIsHolding(false);
    }

    if (!latestState) return;
    const normalized = sortedState(latestState);
    const highestSeen = highestSeenSeqRef.current;

    if (highestSeen === null || normalized.lastSeq < highestSeen) {
      highestSeenSeqRef.current = normalized.lastSeq;
      setQueue([]);
      setIsHolding(false);
      setFrame({ state: normalized, animatedStepSeq: null });
      return;
    }

    const newSteps = normalized.steps.filter((step) => step.seq > highestSeen);
    if (newSteps.length === 0) return;

    highestSeenSeqRef.current = Math.max(
      normalized.lastSeq,
      newSteps[newSteps.length - 1]?.seq ?? highestSeen,
    );
    setQueue((current) => [
      ...current,
      ...newSteps.map((step) => frameThroughStep(normalized, step)),
    ]);
  }, [latestState, streamKey]);

  useEffect(() => {
    if (isHolding || queue.length === 0) return;
    const next = queue[0];
    if (!next) return;

    setQueue((current) => current.slice(1));
    setFrame(next);
    setIsHolding(true);
  }, [isHolding, queue]);

  useEffect(() => {
    if (!isHolding) return;
    const timer = window.setTimeout(() => setIsHolding(false), FRAME_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [frame?.animatedStepSeq, isHolding]);

  return frame;
}

/**
 * Efir boshlanishigacha qolgan vaqt, har soniyada yangilanadi.
 *
 * O'z taymeri bilan yuradi: holat pollingi (650 ms) marosim mantig'i uchun, sanoq esa undan
 * mustaqil — so'rov uzilib qolsa ham ekrandagi raqamlar to'xtamasligi kerak.
 *
 * CHEKLOV: hisob BRAUZER soatiga tayanadi. OBS mashinasining soati sursa, sanoq ham shuncha
 * suradi — server bilan sinxronlash mexanizmi yo'q.
 */
function useRemainingMs(targetIso: string): number {
  const targetMs = useMemo(() => Date.parse(targetIso), [targetIso]);
  const [remainingMs, setRemainingMs] = useState(() => targetMs - Date.now());

  useEffect(() => {
    setRemainingMs(targetMs - Date.now());
    const timer = window.setInterval(
      () => setRemainingMs(targetMs - Date.now()),
      COUNTDOWN_TICK_MS,
    );
    return () => window.clearInterval(timer);
  }, [targetMs]);

  return remainingMs;
}

/** `HH:MM:SS` bir soatdan ko'p qolganda, aks holda `MM:SS`. */
function formatCountdown(remainingMs: number): string {
  // Yuqoriga yaxlitlash: oxirgi soniya `00:01` bo'lib turadi va nolga aynan efir vaqtida yetadi.
  const totalSeconds = Math.ceil(remainingMs / 1_000);
  const pad = (value: number) => String(value).padStart(2, '0');
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE) % SECONDS_PER_MINUTE;
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);

  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Marosim boshlanmagunicha turadigan kadr.
 *
 * Operator OBS sahnasini efirdan ancha oldin ochadi. Birinchi bosqichgacha overlayda hech narsa
 * yo'q edi — ya'ni tomoshabin bo'sh ekranga qarab turardi. Sovg'a va sanoq shu bo'shliqni
 * to'ldiradi va birinchi bosqich kelishi bilan o'rnini marosimga bo'shatib beradi.
 */
function BroadcastPreShow({ draw }: { draw: BroadcastDrawState['draw'] }) {
  const remainingMs = useRemainingMs(draw.scheduledAt);
  // Efir kechikib boshlanishi odatiy hol: manfiy sanoq o'rniga «boshlanmoqda» turadi.
  const countdown = remainingMs > 0 ? formatCountdown(remainingMs) : null;

  return (
    <section className="broadcast-preshow" aria-label={strings.overlay.preShowLabel}>
      <p className="broadcast-eyebrow">{strings.overlay.preShowEyebrow}</p>
      <p className="broadcast-preshow__prize">{draw.prizeLabel}</p>
      {countdown === null ? (
        <p className="broadcast-preshow__starting">{strings.overlay.preShowStarting}</p>
      ) : (
        /*
         * Sanoq `aria-live` mintaqasi ichida va soniyasiga bir marta o'zgaradi — ochiq
         * qoldirilsa ekran o'quvchi raqamlarni soniya sayin qayta o'qib berardi. Shuning uchun
         * o'zgaruvchan qismi a11y daraxtidan olib tashlangan, kartaning ma'nosi esa uning
         * barqaror `aria-label` i orqali bir marta yetkaziladi.
         */
        <p className="broadcast-preshow__countdown" aria-hidden="true">
          {countdown}
        </p>
      )}
    </section>
  );
}

function TicketCodeSlots({
  code,
  newlyRevealedIndex,
}: {
  code: string;
  newlyRevealedIndex: number;
}) {
  const characters = Array.from(code);
  return (
    <div
      className="broadcast-code"
      aria-label={code}
      style={{ gridTemplateColumns: `repeat(${characters.length}, minmax(0, 1fr))` }}
    >
      {characters.map((character, index) => (
        <span
          // A position is stable across successive server snapshots; the character is not.
          key={index}
          className={`broadcast-code__slot ${
            index < FIXED_TICKET_PREFIX_LENGTH
              ? 'broadcast-code__slot--fixed'
              : character === '•'
                ? 'broadcast-code__slot--masked'
                : 'broadcast-code__slot--revealed'
          }${index === newlyRevealedIndex ? ' broadcast-code__slot--new' : ''}`}
          aria-hidden="true"
        >
          {character}
        </span>
      ))}
    </div>
  );
}

/**
 * Kadr — marosim uchun, bu ikki qiymat esa marosimdan tashqarida.
 *
 * `PlaybackFrame` ataylab muzlaydi: bosqichlar navbat bilan, birma-bir efirga chiqishi kerak.
 * Ammo tomoshabinlar soni va efir vaqti hech qaysi bosqichdan kelib chiqmaydi — server ularni
 * har so'rovda qaytadan hisoblaydi. Ular kadr ichida qolsa, «jonli» chip keyingi bosqichgacha
 * qotib turardi (pre-show'da esa — butun kutish davomida), qayta jadvalga qo'yilgan efir esa
 * eski vaqtdan sanashda davom etardi.
 */
interface LiveFacts {
  viewerCount: number;
  /** Faqat pre-show uchun: kadrdagi `draw` ni to'liq almashtirish marosim maydonlarini surardi. */
  preShowDraw: BroadcastDrawState['draw'];
}

function BroadcastStateView({ frame, live }: { frame: PlaybackFrame; live: LiveFacts }) {
  const { state, animatedStepSeq } = frame;
  const ticketCount = ticketCountFromState(state);
  const progressive = latestProgressiveState(state.steps);
  const winnerAnnounced = hasStep(state, DrawRevealKind.WINNER);
  /**
   * OBS ekranida — ENG OXIRGI e'lon qilingan g'olib.
   *
   * Overlay bitta lahzani ko'rsatadigan yuza: bir nechta g'olibli efirda ham ekranda hozir
   * aytilgani turishi kerak, oldingilari emas. Ro'yxatning to'liq holati konsolda ko'rinadi.
   */
  const winner = winnerAnnounced ? (state.winners.at(-1) ?? null) : null;
  const maskedCode = progressive
    ? `${progressive.payload.codePrefix}${'•'.repeat(
        Math.max(0, progressive.payload.totalCharacters - progressive.payload.revealedCharacters),
      )}`
    : null;
  const progress = progressive
    ? (progressive.payload.revealedCharacters / progressive.payload.totalCharacters) * 100
    : 0;
  /*
   * Kulminatsiya — yangi bosqich emas, o'sha SHORTLIST yozuvining boshqacha o'qilishi. Chegara
   * `@qaytim/shared` da turadi, chunki ayni bir marosimni ikki mustaqil ekran chizadi: Mini App
   * (D3) va shu overlay. Shartni bu yerda qayta yozish telefonda «tasdiqlanmoqda», televizorda
   * esa «uch nomzod qoldi» turishiga olib kelardi.
   */
  const culmination =
    progressive !== null && progressive.payload.candidateCount <= DRAW_CULMINATION_CANDIDATE_MAX;
  // Marosim boshlanmagan: birorta ham bosqich yo'q, ya'ni ekranda ko'rsatiladigan holat ham yo'q.
  const preShow = state.steps.length === 0 && !winnerAnnounced;

  return (
    <div className="broadcast-overlay__safe" aria-live="polite">
      {/*
       * Nol tomoshabin CHIZILMAYDI. «Nol kishi tomosha qilmoqda» — soket qatlami javob
       * bermaganda ham chiqadigan yozuv, ya'ni u efirni faqat obro'sizlantiradi; chipning
       * yo'qligi esa hech qanday ma'lumot yo'qotmaydi.
       */}
      {live.viewerCount > 0 ? (
        <section className="broadcast-viewers" aria-label={strings.overlay.viewersLabel}>
          <span className="broadcast-viewers__pulse" aria-hidden="true" />
          {/*
           * Sanoq kartasidagi bilan bir xil sabab: raqam har pollingda (650 ms) o'zgaradi va u
           * `aria-live` mintaqasi ichida turibdi — ochiq qoldirilsa ekran o'quvchi soniyasiga bir
           * necha marta yangi sonni o'qib berardi. Chipning ma'nosi barqaror `aria-label` orqali
           * bir marta yetkaziladi.
           */}
          <p className="broadcast-viewers__value" aria-hidden="true">
            {live.viewerCount.toLocaleString('uz-UZ')}
            <span>{strings.overlay.viewers}</span>
          </p>
        </section>
      ) : null}

      {preShow ? <BroadcastPreShow draw={live.preShowDraw} /> : null}

      {ticketCount !== null ? (
        <section className="broadcast-count" aria-label={strings.overlay.ticketCount}>
          <span className="broadcast-count__mark" aria-hidden="true" />
          <div>
            <p className="broadcast-eyebrow">{strings.overlay.ticketCount}</p>
            <p className="broadcast-count__value">
              {ticketCount.toLocaleString('uz-UZ')}
              <span>{strings.overlay.tickets}</span>
            </p>
          </div>
        </section>
      ) : null}

      {progressive && maskedCode && !winnerAnnounced ? (
        <section
          className={`broadcast-reveal${culmination ? ' broadcast-reveal--culmination' : ''}`}
        >
          <div className="broadcast-reveal__heading">
            <div>
              <p className="broadcast-eyebrow">
                {culmination
                  ? strings.overlay.revealCulminationHeading
                  : strings.overlay.revealHeading}
              </p>
              <p className="broadcast-reveal__round">
                {progressive.payload.revealedCharacters} / {progressive.payload.totalCharacters}
              </p>
            </div>
            {culmination ? (
              <p className="broadcast-candidates broadcast-candidates--culmination">
                {strings.overlay.revealCulminationCandidates}
              </p>
            ) : (
              <div className="broadcast-candidates">
                <span>{strings.overlay.candidates}</span>
                <strong>{progressive.payload.candidateCount.toLocaleString('uz-UZ')}</strong>
              </div>
            )}
          </div>

          <TicketCodeSlots
            code={maskedCode}
            newlyRevealedIndex={
              animatedStepSeq === progressive.seq
                ? FIXED_TICKET_PREFIX_LENGTH + progressive.payload.revealedCharacters - 1
                : -1
            }
          />

          <div
            className="broadcast-progress"
            role="progressbar"
            aria-label={strings.overlay.progress}
            aria-valuemin={0}
            aria-valuemax={progressive.payload.totalCharacters}
            aria-valuenow={progressive.payload.revealedCharacters}
          >
            <span style={{ width: `${progress}%` }} />
          </div>
        </section>
      ) : null}

      {winner ? (
        <section className="broadcast-winner" key={state.lastSeq}>
          <div className="broadcast-winner__accent" aria-hidden="true" />
          <p className="broadcast-winner__label">{strings.overlay.winner}</p>
          <p className="broadcast-winner__name">{winner.userDisplayName}</p>
          <p className="broadcast-winner__ticket">{winner.ticketCode}</p>
          {/*
            Ekranga chiqadigan sovrin — SHU g'olibniki, bo'lmasa efirning umumiy sovrini.
            Ko'p sovrinli efirda `draw.prizeLabel` kartadagi yig'ma matn ("3 ta sovrin"), ya'ni
            uni g'olibning tagiga yozish OBS kadrida yolg'on bo'lardi.
          */}
          <p className="broadcast-winner__prize">{winner.prizeLabel ?? state.draw.prizeLabel}</p>
        </section>
      ) : null}
    </div>
  );
}

/** Public, chrome-free OBS Browser Source. Access is scoped by the fragment token. */
export function BroadcastOverlayPage() {
  const { drawId } = useParams({ from: '/overlay/$drawId' });
  const hash = useLocation({ select: (location) => location.hash });
  const accessToken = overlayTokenFromHash(hash);

  useLayoutEffect(() => {
    document.documentElement.classList.add('broadcast-overlay-mode');
    document.body.classList.add('broadcast-overlay-mode');
    return () => {
      document.documentElement.classList.remove('broadcast-overlay-mode');
      document.body.classList.remove('broadcast-overlay-mode');
    };
  }, []);

  const state = useQuery({
    queryKey: ['broadcast-overlay', drawId],
    queryFn: ({ signal }) => fetchBroadcastDrawState(drawId, accessToken as string, signal),
    enabled: accessToken !== null,
    retry: false,
    staleTime: 0,
    refetchInterval: BROADCAST_OVERLAY_POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });
  const playbackFrame = useBroadcastPlayback(
    state.data,
    `${drawId}:${accessToken === null ? 'missing' : 'authorized'}`,
  );
  const visibleFrame =
    playbackFrame ??
    (state.data ? { state: sortedState(state.data), animatedStepSeq: null } : null);
  /*
   * So'rov uzilib qolsa `state.data` eskiradi (React Query oxirgi muvaffaqiyatli javobni ushlab
   * turadi), lekin kadr undan ham eski bo'lishi mumkin — shuning uchun tirik javob birinchi,
   * kadr esa zaxira.
   */
  const live: LiveFacts | null = visibleFrame
    ? {
        viewerCount: state.data?.viewerCount ?? visibleFrame.state.viewerCount,
        preShowDraw: state.data?.draw ?? visibleFrame.state.draw,
      }
    : null;

  if (!accessToken) {
    return (
      <main className="broadcast-overlay" data-testid="broadcast-overlay">
        <p className="broadcast-overlay__notice" role="alert">
          {strings.overlay.missingAccess}
        </p>
      </main>
    );
  }

  if (visibleFrame && live) {
    return (
      <main className="broadcast-overlay" data-testid="broadcast-overlay">
        <BroadcastStateView frame={visibleFrame} live={live} />
      </main>
    );
  }

  if (state.isPending) {
    return (
      <main className="broadcast-overlay" data-testid="broadcast-overlay">
        <p className="broadcast-overlay__notice">{strings.overlay.connecting}</p>
      </main>
    );
  }

  if (state.isError || !state.data) {
    return (
      <main className="broadcast-overlay" data-testid="broadcast-overlay">
        <p className="broadcast-overlay__notice" role="alert">
          {strings.overlay.unavailable}
        </p>
      </main>
    );
  }

  return (
    <main className="broadcast-overlay" data-testid="broadcast-overlay">
      <p className="broadcast-overlay__notice">{strings.overlay.connecting}</p>
    </main>
  );
}
